import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabaseAdmin, getSupabaseInitError } from '../config/supabase';
import toast from 'react-hot-toast';
import { logAction, SECTIONS, ACTIONS } from '../services/auditLog';
import { setLastActivityNow, clearInactivityStorage } from '../hooks/useInactivityLogout';
import { getEffectiveAllowedRoutes, userCanAccessPanelPath } from '../config/panelRoutes';
import { verifyPassword } from '../utils/passwordHash';
import { isProxyEnvAvailable } from '../utils/supabaseProxySetting';
import { loginViaAdminPhp } from '../services/adminLogin';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const validateSessionToken = useCallback(async (session) => {
    if (!session?.user?.id || !session?.token) return false;
    try {
      const { data, error } = await supabaseAdmin
        .from('admin_users')
        .select('current_session_token')
        .eq('id', session.user.id)
        .single();
      if (error || data == null) return true;
      if (data.current_session_token == null) return true;
      return data.current_session_token === session.token;
    } catch {
      return true;
    }
  }, []);

  const checkSession = useCallback(async () => {
    try {
      const initErr = getSupabaseInitError();
      if (initErr) {
        setLoading(false);
        return;
      }
      const sessionData = localStorage.getItem('admin_session');
      if (sessionData) {
        const session = JSON.parse(sessionData);
        if (session.user && session.token) {
          const stillValid = await validateSessionToken(session);
          if (!stillValid) {
            localStorage.removeItem('admin_session');
            clearInactivityStorage();
            setUser(null);
            toast.success('Zostałeś wylogowany – zalogowano na to konto z innego urządzenia.');
          } else {
            setUser(session.user);
          }
        } else {
          localStorage.removeItem('admin_session');
        }
      }
    } catch (error) {
      console.error('Error checking session:', error);
      localStorage.removeItem('admin_session');
    } finally {
      setLoading(false);
    }
  }, [validateSessionToken]);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // Okresowa weryfikacja sesji – wylogowanie, gdy ktoś inny zalogował się na to konto
  useEffect(() => {
    if (!user) return;
    const intervalMs = 45 * 1000;
    const intervalId = setInterval(async () => {
      const sessionData = localStorage.getItem('admin_session');
      if (!sessionData) return;
      try {
        const session = JSON.parse(sessionData);
        if (!session?.user || !session?.token) return;
        const stillValid = await validateSessionToken(session);
        if (!stillValid) {
          localStorage.removeItem('admin_session');
          clearInactivityStorage();
          setUser(null);
          toast.success('Zostałeś wylogowany – zalogowano na to konto z innego urządzenia.');
        }
      } catch {
        // ignore
      }
    }, intervalMs);
    return () => clearInterval(intervalId);
  }, [user, validateSessionToken]);

  const login = async (email, password) => {
    try {
      const normalizedEmail = email.toLowerCase().trim();
      const normalizedPassword = password.trim();

      if (isProxyEnvAvailable()) {
        const loginResult = await loginViaAdminPhp(normalizedEmail, normalizedPassword);
        const session = {
          user: {
            id: loginResult.user.id,
            email: loginResult.user.email,
            name: loginResult.user.name,
            role: loginResult.user.role || 'admin',
            panel_allowed_routes: loginResult.user.panel_allowed_routes ?? null,
          },
          token: loginResult.token,
          expiresAt: loginResult.expiresAt,
        };
        localStorage.setItem('admin_session', JSON.stringify(session));
        setLastActivityNow();
        setUser(session.user);
        await logAction(
          {
            section: SECTIONS.LOGIN,
            action: ACTIONS.LOGIN,
            details: 'Zalogowano do panelu administratora.',
          },
          session.user
        );
        toast.success('Zalogowano pomyślnie');
        return { success: true, user: session.user };
      }

      // Tryb bezpośredni (lokalny): supabaseAdmin + service role w .env
      const { data: adminUser, error: fetchError } = await supabaseAdmin
        .from('admin_users')
        .select(
          'id, email, name, role, is_active, panel_allowed_routes, password_hash',
        )
        .eq('email', normalizedEmail)
        .eq('is_active', true)
        .single();

      if (fetchError) {
        console.error('Supabase fetch error:', fetchError);
        throw new Error('Nieprawidłowy email lub hasło');
      }

      if (!adminUser) {
        throw new Error('Nieprawidłowy email lub hasło');
      }

      const passwordMatch = await verifyPassword(
        normalizedPassword,
        adminUser.password_hash,
      );

      if (!passwordMatch) {
        throw new Error('Nieprawidłowy email lub hasło');
      }

      const sessionToken = generateToken(adminUser.id);

      const { error: updateError } = await supabaseAdmin
        .from('admin_users')
        .update({
          last_login: new Date().toISOString(),
          current_session_token: sessionToken,
        })
        .eq('id', adminUser.id);
      if (updateError) {
        await supabaseAdmin
          .from('admin_users')
          .update({ last_login: new Date().toISOString() })
          .eq('id', adminUser.id);
      }

      const session = {
        user: {
          id: adminUser.id,
          email: adminUser.email,
          name: adminUser.name,
          role: adminUser.role || 'admin',
          panel_allowed_routes: adminUser.panel_allowed_routes ?? null,
        },
        token: sessionToken,
        expiresAt: Date.now() + 7 * 24 * 60 * 60 * 1000,
      };

      localStorage.setItem('admin_session', JSON.stringify(session));
      setLastActivityNow();
      setUser(session.user);

      await logAction(
        {
          section: SECTIONS.LOGIN,
          action: ACTIONS.LOGIN,
          details: 'Zalogowano do panelu administratora.',
        },
        session.user
      );

      toast.success('Zalogowano pomyślnie');
      return { success: true, user: session.user };
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || 'Błąd logowania');
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_session');
    clearInactivityStorage();
    setUser(null);
    toast.success('Wylogowano pomyślnie');
  };

  // Generuj prosty token (w produkcji użyj JWT)
  const generateToken = (userId) => {
    return btoa(`${userId}:${Date.now()}:${Math.random()}`);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    /** Pełne uprawnienia w otwartych zakładkach: rola admin lub jawna lista panel_allowed_routes. */
    isAdmin:
      user?.role === 'admin' ||
      (Array.isArray(user?.panel_allowed_routes) && user.panel_allowed_routes.length > 0),
    /** Wyłącznie rola `admin` w bazie — wrażliwe dane (np. lista urządzeń w powiadomieniach). */
    isAdministrator: user?.role === 'admin',
    isModerator: user?.role === 'moderator',
    isSupport: user?.role === 'support',
    isSecretary: user?.role === 'secretary',
    hasRole: (role) => user?.role === role,
    hasAnyRole: (roles) => roles.includes(user?.role),
    canAccessRoute: (path) => userCanAccessPanelPath(user, path),
    getAllowedRoutes: () => getEffectiveAllowedRoutes(user),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
